import {Dropdown, DropdownDivider, DropdownItem, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {useState, type ReactNode} from 'react';

import {isNotNil, isTruthy} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {PRESET_TAB_STYLES} from '../../constants/PRESET_TAB_STYLES';
import {getPresetColorClasses} from '../../utils/getPresetColorClasses';
import {PresetActionsButton} from '../PresetActionsButton/PresetActionsButton';
import {PresetPinIcon} from '../PresetPinIcon/PresetPinIcon';
import {PresetSelectionIndicator} from '../PresetSelectionIndicator/PresetSelectionIndicator';
import {PresetTabLabel} from '../PresetTabLabel/PresetTabLabel';

type PinnedPresetTabProps = {
  label: string;
  color: string;
  count: number | undefined;
  active: boolean;
  unsaved?: boolean;
  isLoading?: boolean;
  onClick: () => void;
  onTogglePin?: () => void;
  actions?: ReactNode | ((closeMenu: () => void) => ReactNode);
  copyIsLoading?: boolean;
  'data-testid'?: string;
};

export function PinnedPresetTab(props: PinnedPresetTabProps) {
  const t = useTranslationContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hasActions = isTruthy(props.actions) || isNotNil(props.onTogglePin);

  return (
    <div
      className={`${PRESET_TAB_STYLES.root} ${getPresetColorClasses(props.color, props.active)}`}
      data-testid={props['data-testid']}
    >
      <button
        type="button"
        onClick={props.onClick}
        aria-pressed={props.active}
        aria-busy={props.isLoading || undefined}
        className={PRESET_TAB_STYLES.button}
      >
        <PresetSelectionIndicator
          active={props.active}
          data-testid={suffixTestId('active-indicator', props)}
        />
        <PresetTabLabel label={props.label} active={props.active} />
        <Show when={!props.isLoading && props.unsaved}>
          <span className="font-bold">*</span>
        </Show>
        <Show when={props.isLoading || props.count !== undefined || hasActions}>
          <span className="relative inline-flex shrink-0 items-center justify-end">
            <span
              aria-hidden={props.isLoading || undefined}
              className={`${PRESET_TAB_STYLES.count} ${PRESET_TAB_STYLES.countColor} ${props.isLoading ? 'invisible' : ''}`}
            >
              {props.count}
            </span>
            <Show when={props.isLoading}>
              <span className="absolute inset-0 flex items-center justify-center">
                <PinnedPresetSpinner />
              </span>
            </Show>
          </span>
        </Show>
      </button>

      <Show when={hasActions}>
        <div
          inert={props.isLoading}
          aria-hidden={props.isLoading || undefined}
          className={`${PRESET_TAB_STYLES.actions} ${isMenuOpen ? '' : PRESET_TAB_STYLES.hiddenActions} ${props.isLoading ? 'invisible pointer-events-none!' : ''}`}
        >
          <Dropdown
            isLazy
            isOpen={isMenuOpen}
            onOpen={() => setIsMenuOpen(true)}
            onClose={() => {
              if (!props.copyIsLoading) {
                setIsMenuOpen(false);
              }
            }}
            dropdownControl={
              <PresetActionsButton
                variant="tab"
                isDisabled={props.isLoading}
                isExpanded={isMenuOpen}
              />
            }
          >
            <Show when={isMenuOpen}>
              {typeof props.actions === 'function'
                ? props.actions(() => setIsMenuOpen(false))
                : props.actions}
            </Show>
            <Show when={isTruthy(props.actions) && isNotNil(props.onTogglePin)}>
              <DropdownDivider />
            </Show>
            <Show when={props.onTogglePin}>
              {(onTogglePin) => (
                <DropdownItem
                  onClick={onTogglePin}
                  prefix={<PresetPinIcon active />}
                  label={t('general.actions.unpin')}
                />
              )}
            </Show>
          </Dropdown>
        </div>
      </Show>
    </div>
  );
}

function PinnedPresetSpinner() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      aria-label="Loading"
      className="animate-spin"
    >
      <circle cx={12} cy={12} r={9} opacity={0.25} />
      <path d="M21 12a9 9 0 0 0-9-9" />
    </svg>
  );
}
